import React, { useState } from 'react';
import Turnstile from '../components/Turnstile/Turnstile';
import { registerAccount, changePassword } from '../utils/api';
import styles from './Account.module.css';

/* ---- Validators ---- */
function validateRegister({ login, email, password, confirmPassword }) {
  const e = {};
  if (!login) e.login = 'Login is required.';
  else if (!/^[a-zA-Z0-9]{4,16}$/.test(login)) e.login = 'Login must be 4-16 alphanumeric characters.';
  if (!email) e.email = 'Email is required.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Enter a valid email address.';
  if (!password) e.password = 'Password is required.';
  else if (password.length < 6 || password.length > 16) e.password = 'Password must be 6-16 characters.';
  if (!confirmPassword) e.confirmPassword = 'Please confirm your password.';
  else if (password !== confirmPassword) e.confirmPassword = 'Passwords do not match.';
  return e;
}

function validateChange({ login, currentPassword, newPassword, confirmNewPassword }) {
  const e = {};
  if (!login) e.login = 'Login is required.';
  if (!currentPassword) e.currentPassword = 'Current password is required.';
  if (!newPassword) e.newPassword = 'New password is required.';
  else if (newPassword.length < 6 || newPassword.length > 16) e.newPassword = 'New password must be 6-16 characters.';
  if (!confirmNewPassword) e.confirmNewPassword = 'Please confirm your new password.';
  else if (newPassword !== confirmNewPassword) e.confirmNewPassword = 'Passwords do not match.';
  return e;
}

/* ---- Register Sub-form ---- */
function RegisterForm() {
  const [fields, setFields] = useState({ login: '', email: '', password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [captchaReset, setCaptchaReset] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFields(p => ({ ...p, [name]: value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    const ve = validateRegister(fields);
    if (Object.keys(ve).length) { setErrors(ve); return; }
    if (!turnstileToken) { setStatus({ type: 'error', message: 'Please complete the bot verification.' }); return; }
    setLoading(true);
    try {
      await registerAccount({ login: fields.login, password: fields.password, email: fields.email, turnstileToken });
      setStatus({ type: 'success', message: 'Account created successfully. You may now log in via the game client.' });
      setFields({ login: '', email: '', password: '', confirmPassword: '' });
      setTurnstileToken(null);
      setCaptchaReset(p => !p);
    } catch (err) {
      setStatus({ type: 'error', message: err.message || 'Registration failed. Please try again.' });
      setTurnstileToken(null);
      setCaptchaReset(p => !p);
    } finally { setLoading(false); }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      {status && (
        <div className={`${styles.statusMsg} ${status.type === 'success' ? styles.statusSuccess : styles.statusError}`} role="alert">
          {status.message}
        </div>
      )}
      <div className={styles.field}>
        <label className={styles.label} htmlFor="reg-login">Login</label>
        <input id="reg-login" type="text" name="login" value={fields.login} onChange={handleChange}
          className={`${styles.input} ${errors.login ? styles.inputError : ''}`}
          autoComplete="username" placeholder="4-16 alphanumeric characters" maxLength={16} />
        {errors.login && <span className={styles.fieldError} role="alert">{errors.login}</span>}
      </div>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="reg-email">Email</label>
        <input id="reg-email" type="email" name="email" value={fields.email} onChange={handleChange}
          className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
          autoComplete="email" placeholder="your@email.com" />
        {errors.email && <span className={styles.fieldError} role="alert">{errors.email}</span>}
      </div>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="reg-password">Password</label>
        <input id="reg-password" type="password" name="password" value={fields.password} onChange={handleChange}
          className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
          autoComplete="new-password" placeholder="6-16 characters" maxLength={16} />
        {errors.password && <span className={styles.fieldError} role="alert">{errors.password}</span>}
      </div>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="reg-confirm">Confirm Password</label>
        <input id="reg-confirm" type="password" name="confirmPassword" value={fields.confirmPassword} onChange={handleChange}
          className={`${styles.input} ${errors.confirmPassword ? styles.inputError : ''}`}
          autoComplete="new-password" placeholder="Repeat your password" maxLength={16} />
        {errors.confirmPassword && <span className={styles.fieldError} role="alert">{errors.confirmPassword}</span>}
      </div>
      <div className={styles.field}>
        <label className={styles.label}>Verification</label>
        <Turnstile onToken={setTurnstileToken} reset={captchaReset} />
        {!turnstileToken && status?.type === 'error' && status.message.includes('verification') && (
          <span className={styles.fieldError} role="alert">Please complete the bot verification.</span>
        )}
      </div>
      <button type="submit" className={styles.submitBtn} disabled={loading} aria-busy={loading}>
        {loading ? 'Creating Account...' : 'Create Account'}
      </button>
    </form>
  );
}

/* ---- Change Password Sub-form ---- */
function ChangePasswordForm() {
  const [fields, setFields] = useState({ login: '', currentPassword: '', newPassword: '', confirmNewPassword: '' });
  const [errors, setErrors] = useState({});
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [captchaReset, setCaptchaReset] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFields(p => ({ ...p, [name]: value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    const ve = validateChange(fields);
    if (Object.keys(ve).length) { setErrors(ve); return; }
    if (!turnstileToken) { setStatus({ type: 'error', message: 'Please complete the bot verification.' }); return; }
    setLoading(true);
    try {
      await changePassword({ login: fields.login, currentPassword: fields.currentPassword, newPassword: fields.newPassword, turnstileToken });
      setStatus({ type: 'success', message: 'Password updated successfully.' });
      setFields({ login: '', currentPassword: '', newPassword: '', confirmNewPassword: '' });
      setTurnstileToken(null);
      setCaptchaReset(p => !p);
    } catch (err) {
      setStatus({ type: 'error', message: err.message || 'Failed to update password. Please try again.' });
      setTurnstileToken(null);
      setCaptchaReset(p => !p);
    } finally { setLoading(false); }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      {status && (
        <div className={`${styles.statusMsg} ${status.type === 'success' ? styles.statusSuccess : styles.statusError}`} role="alert">
          {status.message}
        </div>
      )}
      <div className={styles.field}>
        <label className={styles.label} htmlFor="cp-login">Login</label>
        <input id="cp-login" type="text" name="login" value={fields.login} onChange={handleChange}
          className={`${styles.input} ${errors.login ? styles.inputError : ''}`}
          autoComplete="username" placeholder="Your account login" />
        {errors.login && <span className={styles.fieldError} role="alert">{errors.login}</span>}
      </div>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="cp-current">Current Password</label>
        <input id="cp-current" type="password" name="currentPassword" value={fields.currentPassword} onChange={handleChange}
          className={`${styles.input} ${errors.currentPassword ? styles.inputError : ''}`}
          autoComplete="current-password" placeholder="Your current password" />
        {errors.currentPassword && <span className={styles.fieldError} role="alert">{errors.currentPassword}</span>}
      </div>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="cp-new">New Password</label>
        <input id="cp-new" type="password" name="newPassword" value={fields.newPassword} onChange={handleChange}
          className={`${styles.input} ${errors.newPassword ? styles.inputError : ''}`}
          autoComplete="new-password" placeholder="6-16 characters" maxLength={16} />
        {errors.newPassword && <span className={styles.fieldError} role="alert">{errors.newPassword}</span>}
      </div>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="cp-confirm">Confirm New Password</label>
        <input id="cp-confirm" type="password" name="confirmNewPassword" value={fields.confirmNewPassword} onChange={handleChange}
          className={`${styles.input} ${errors.confirmNewPassword ? styles.inputError : ''}`}
          autoComplete="new-password" placeholder="Repeat your new password" maxLength={16} />
        {errors.confirmNewPassword && <span className={styles.fieldError} role="alert">{errors.confirmNewPassword}</span>}
      </div>
      <div className={styles.field}>
        <label className={styles.label}>Verification</label>
        <Turnstile onToken={setTurnstileToken} reset={captchaReset} />
        {!turnstileToken && status?.type === 'error' && status.message.includes('verification') && (
          <span className={styles.fieldError} role="alert">Please complete the bot verification.</span>
        )}
      </div>
      <button type="submit" className={styles.submitBtn} disabled={loading} aria-busy={loading}>
        {loading ? 'Updating...' : 'Change Password'}
      </button>
    </form>
  );
}

/* ---- Main Account Page ---- */
export default function Account() {
  const [tab, setTab] = useState('create');

  return (
    <main className={styles.main}>
      <section className={`page-hero ${styles.hero}`} aria-label="Account management">
        <h1>{tab === 'create' ? 'Create Account' : 'Change Password'}</h1>
        <p className={styles.heroSub}>
          {tab === 'create' ? 'Begin your journey in Aden' : 'Update your account credentials'}
        </p>
      </section>

      <div className={styles.container}>
        <div className={styles.tabBar} role="tablist" aria-label="Account options">
          <button
            role="tab"
            aria-selected={tab === 'create'}
            className={`${styles.tab} ${tab === 'create' ? styles.tabActive : ''}`}
            onClick={() => setTab('create')}
          >
            Create Account
          </button>
          <button
            role="tab"
            aria-selected={tab === 'change'}
            className={`${styles.tab} ${tab === 'change' ? styles.tabActive : ''}`}
            onClick={() => setTab('change')}
          >
            Change Password
          </button>
        </div>

        {tab === 'create' ? (
          <>
            <RegisterForm />
            <p className={styles.switchPrompt}>
              Already registered?{' '}
              <button type="button" className={styles.switchLink} onClick={() => setTab('change')}>
                Change your password
              </button>
            </p>
          </>
        ) : (
          <>
            <ChangePasswordForm />
            <p className={styles.switchPrompt}>
              Not yet a warrior?{' '}
              <button type="button" className={styles.switchLink} onClick={() => setTab('create')}>
                Create a free account
              </button>
            </p>
          </>
        )}
      </div>
    </main>
  );
}
